import * as THREE from 'three';
import { Entity } from '../entities/Entity.js';
import { CONFIG } from '../config.js';

/**
 * @typedef {Object} CloudOptions
 * @property {THREE.Vector3} [position] - Initial position
 */

/**
 * A volumetric cloud made of multiple puffs (spheres).
 * Drifts across the sky.
 */
export class Cloud extends Entity {
    /**
     * @param {CloudOptions} [options]
     */
    constructor(options = {}) {
        super({
            position: options.position,
            collisionRadius: 0,
            collidable: false,
        });

        this.#buildMesh();
    }

    /**
     * Build the cloud from multiple sphere puffs
     */
    #buildMesh() {
        const puffCount = THREE.MathUtils.randInt(
            CONFIG.cloud.puffCountMin,
            CONFIG.cloud.puffCountMax
        );

        for (let i = 0; i < puffCount; i++) {
            const puffGeo = new THREE.SphereGeometry(
                THREE.MathUtils.randFloat(CONFIG.cloud.puffSizeMin, CONFIG.cloud.puffSizeMax),
                8,
                8
            );

            const puffMat = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: THREE.MathUtils.randFloat(CONFIG.cloud.opacityMin, CONFIG.cloud.opacityMax),
                roughness: 1.0,
                metalness: 0.0,
                emissive: 0xffffff,
                emissiveIntensity: 0.4
            });

            const puff = new THREE.Mesh(puffGeo, puffMat);
            puff.position.set(
                THREE.MathUtils.randFloatSpread(20),
                THREE.MathUtils.randFloatSpread(5),
                THREE.MathUtils.randFloatSpread(20)
            );
            this.mesh.add(puff);
        }

        // Random scale for variety
        const cloudScale = THREE.MathUtils.randFloat(CONFIG.cloud.scaleMin, CONFIG.cloud.scaleMax);
        this.mesh.scale.set(cloudScale, cloudScale * 0.7, cloudScale);
    }

    /**
     * Update cloud (drift across sky)
     * @param {number} delta - Time since last frame in seconds
     */
    update(delta) {
        // Drift eastward
        this.position.x += CONFIG.cloud.driftSpeed * delta;

        // Wrap around when too far east
        if (this.position.x > 200) {
            this.position.x = -200;
        }
    }
}
